<script setup>
/**
 * Componente Tabla
 *
 * Este componente proporciona una tabla personalizable que gestiona la
 * lógica de ordenación de columnas. Expone mediante provide el estado de
 * ordenación actual y una función para cambiar el orden, permitiendo que
 * los componentes hijos (como cabeceras de tabla) accedan y modifiquen la
 * ordenación.
 *
 * Props:
 * - campo: Nombre del campo por el que se ordena inicialmente.
 * - direccion: Dirección inicial de la ordenación ('asc' o 'desc').
 *
 * Eventos:
 * - ordenar: Se emite cada vez que cambia la ordenación, enviando el nuevo
 *   estado.
 *
 * Uso:
 * <Tabla campo="nombre" direccion="asc">
 *   <!-- Columnas y filas aquí -->
 * </Tabla>
 */
import { provide, reactive, watch } from 'vue';

const emits = defineEmits(['ordenar', 'update:filtros']);
const props = defineProps({
  campo: { type: String, default: null },
  direccion: { type: String, default: null },
  filtros: { type: Object, default: () => ({}) }, // Para v-model:filtros
});

const ordenacion = reactive({
  campo: props.campo,
  direccion: props.direccion,
});

// Estado reactivo local para filtros, sincronizado con la prop
const filtros = reactive({ ...props.filtros });

// Sincroniza cambios de la prop filtros al estado local
watch(
  () => props.filtros,
  (nuevo) => {
    Object.assign(filtros, nuevo || {});
  },
  { deep: true },
);

// Emite update:filtros cuando cambian los filtros locales
watch(
  filtros,
  (nuevosFiltros) => {
    emits('update:filtros', { ...nuevosFiltros });
  },
  { deep: true },
);

function ordenar(campo) {
  if (campo === ordenacion.campo) {
    ordenacion.direccion = ordenacion.direccion === 'asc' ? 'desc' : 'asc';
  }
  ordenacion.campo = campo;
  emits('ordenar', ordenacion);
}
// Publicamos los datos de ordenación para que los cojan las cabeceras
provide('ordenacion', ordenacion);
provide('ordenar', ordenar);
provide('filtros', filtros);
</script>

<template>
  <table>
    <slot></slot>
  </table>
</template>
